import { z } from 'zod';
import type { FacetOption } from '@/components/data-table/data-table-faceted-filter';
import { sortSearchParam } from '@/components/data-table/tableSort';
import { foldVi } from '@/features/timesheet/review/reviewFilters';
import type { TaskRow } from '../api/workApi.types';
import { isTaskStatus, STATUS_LABEL, STATUS_ORDER, type TaskStatus } from './taskStatus';

/** Giá trị đại diện "Văn phòng" (projectId = null) trong URL và facet. */
export const OFFICE = 'office';
const GONE_LABEL = 'Không còn trong danh sách';

export interface TaskFilter {
  scope: 'all' | 'mine';
  q?: string;
  project?: string[];
  person?: string[];
  status?: TaskStatus[];
  issue?: boolean;
}

// Mọi tham số sai (kiểu lạ, giá trị ngoài danh sách) coi như vắng — URL xấu không được làm hỏng route.
// Mặc định (bảng, tất cả) KHÔNG xuất hiện trong kết quả — để router không ghi chúng lên URL.
const only = <T extends string>(value: T) => z.unknown().optional().transform((v) => (v === value ? value : undefined));
const strings = z.unknown().optional().transform((v) => {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === 'string' && x !== '');
  return out.length > 0 ? out : undefined;
});

export const workSearchSchema = z.object({
  view: only('kanban' as const),
  scope: only('mine' as const),
  q: z.unknown().optional().transform((v) => (typeof v === 'string' && v.trim() !== '' ? v : undefined)),
  project: strings,
  person: strings,
  status: z.unknown().optional().transform((v) => {
    if (!Array.isArray(v)) return undefined;
    const out = v.filter(isTaskStatus);
    return out.length > 0 ? out : undefined;
  }),
  issue: z.unknown().optional().transform((v) => (v === true ? (true as const) : undefined)),
  task: z.unknown().optional().transform((v) => (typeof v === 'string' && v !== '' ? v : undefined)),
  edit: z.unknown().optional().transform((v) => (v === true ? (true as const) : undefined)),
  todo: z.unknown().optional().transform((v) => (v === 'new' ? ('new' as const) : undefined)),
  /** Mã dự án chọn sẵn cho form `?task=new` (khác `project`, vốn là bộ lọc theo id). */
  /** Sắp xếp bảng task: `cột.asc|desc` (primitive data-table/useUrlSort). */
  s: sortSearchParam,
  newFor: z.unknown().optional().transform((v) => (typeof v === 'string' && v !== '' ? v : undefined)),
});

export type WorkSearch = z.infer<typeof workSearchSchema>;

/** Bỏ khóa undefined/rỗng để URL sạch. */
export function cleanSearch(s: Partial<WorkSearch>): Partial<WorkSearch> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(s)) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    out[k] = v;
  }
  return out as Partial<WorkSearch>;
}

export const isFiltering =(f: TaskFilter) =>
  f.scope === 'mine' || (f.q ?? '').trim() !== '' || (f.project?.length ?? 0) > 0 || (f.person?.length ?? 0) > 0
  || (f.status?.length ?? 0) > 0 || f.issue === true;

const visible = (r: TaskRow) => isTaskStatus(r.status); // 'skipped' không có cột/nhãn → ẩn

export function filterTasks(rows: TaskRow[], f: TaskFilter, meId: string): TaskRow[] {
  const needle = foldVi((f.q ?? '').trim());
  const projects = new Set(f.project ?? []);
  const people = new Set(f.person ?? []);
  const statuses = new Set(f.status ?? []);
  return rows.filter((r) => {
    if (!visible(r)) return false;
    if (f.scope === 'mine' && !r.assignees.some((a) => a.id === meId)) return false;
    if (needle && !foldVi(`${r.title} ${r.projectCode ?? ''} ${r.projectName ?? 'Văn phòng'}`).includes(needle)) return false;
    if (projects.size > 0 && !projects.has(r.projectId ?? OFFICE)) return false;
    if (people.size > 0 && !r.assignees.some((a) => people.has(a.id))) return false;
    if (statuses.size > 0 && !statuses.has(r.status)) return false;
    if (f.issue && !r.issue) return false;
    return true;
  });
}

function facet(entries: { value: string; label: string }[], selected: string[] = []): FacetOption[] {
  const byValue = new Map<string, FacetOption>();
  for (const e of entries) {
    const cur = byValue.get(e.value);
    if (cur) cur.count = (cur.count ?? 0) + 1; else byValue.set(e.value, { value: e.value, label: e.label, count: 1 });
  }
  const live = [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label, 'vi'));
  const gone = selected.filter((v) => !byValue.has(v)).map((value) => ({ value, label: GONE_LABEL, count: 0 }));
  return [...live, ...gone];
}

export const projectFacet = (rows: TaskRow[], selected: string[] = []) =>
  facet(rows.filter(visible).map((r) => ({ value: r.projectId ?? OFFICE, label: r.projectCode ? `${r.projectCode} · ${r.projectName ?? ''}` : '— · Văn phòng' })), selected);

export const personFacet = (rows: TaskRow[], selected: string[] = []) =>
  facet(rows.filter(visible).flatMap((r) => r.assignees.map((a) => ({ value: a.id, label: a.name }))), selected);

export const statusFacet = (rows: TaskRow[]): FacetOption[] =>
  STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s], count: rows.filter((r) => r.status === s).length }));
